import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Clock, ChevronDown, ChevronRight, Settings, FolderPlus, Database, Globe, Plug, MessageSquare, BookOpen } from 'lucide-react';
import ThreadList from '../chat/ThreadList';
import FolderTree from '../folders/FolderTree';
import CreateFolderModal from '../folders/CreateFolderModal';
import config from '../../config';
import logoSmall from '../../assets/images/logo_64x64.png';

type SidebarTab = 'chats' | 'repository';

const REPOSITORY_PATHS = ['/data-sources', '/global-documents', '/knowledge-base'];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = React.useState<SidebarTab>('chats');
  const [showUnfiled, setShowUnfiled] = React.useState(true);
  const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);

  // When on a repository-related page (e.g. after OAuth callback to data-sources), show Repository tab so the user sees where their data source lives.
  React.useEffect(() => {
    const pathname = location.pathname;
    const isRepositoryPage =
      REPOSITORY_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
    setActiveTab(isRepositoryPage ? 'repository' : 'chats');
  }, [location.pathname]);

  const handleNewThread = () => {
    navigate('/chat');
  };

  const handleCreateFolder = () => {
    setShowCreateFolderModal(true);
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="flex items-center space-x-2 bg-transparent border-0 p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-md"
          >
            <img src={logoSmall} alt={`${config.appName} Logo`} className="w-7 h-7" />
            <span className="text-sm font-medium text-gray-900">{config.appName}</span>
          </button>
        </div>

        {/* Tabs: Chats | Repository */}
        <div className="flex rounded-md bg-gray-100 p-0.5">
          <button
            type="button"
            onClick={() => setActiveTab('chats')}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal transition-colors ${
              activeTab === 'chats'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Chats
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('repository')}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal transition-colors ${
              activeTab === 'repository'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Database className="h-3.5 w-3.5" />
            Repository
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
        {activeTab === 'chats' && (
          <>
            {/* Compact actions */}
            <div className="px-2 py-1.5 flex gap-1.5">
              <button
                type="button"
                onClick={handleNewThread}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-normal bg-primary-600 text-white hover:bg-primary-700"
              >
                <Plus className="h-3.5 w-3.5" />
                New Chat
              </button>
              <button
                type="button"
                onClick={handleCreateFolder}
                className="flex items-center gap-1 py-1.5 px-2 rounded text-xs font-normal border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                Folder
              </button>
            </div>

            {/* Folder tree - direct, no "My Data" wrapper */}
            <div className="px-2 pt-1">
              <FolderTree />
            </div>

            {/* Your Chats Section */}
            <div className="px-2 py-1.5 border-t border-gray-200">
              <button
                onClick={() => setShowUnfiled(!showUnfiled)}
                className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-100 rounded text-xs font-normal text-gray-700"
              >
                <div className="flex items-center space-x-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Your Chats</span>
                </div>
                {showUnfiled ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {showUnfiled && (
                <div className="mt-1">
                  <ThreadList />
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'repository' && (
          <div className="px-2 py-1.5 flex flex-col gap-0">
            {/* Global Data Section */}
            <div className="py-0.5">
              <button
                onClick={() => navigate('/global-documents')}
                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-100 rounded text-xs font-normal text-gray-700"
              >
                <div className="flex items-center space-x-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  <span>Global Data</span>
                </div>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Knowledge Base Section */}
            <div className="py-0.5 border-t border-gray-200">
              <button
                onClick={() => navigate('/knowledge-base')}
                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-100 rounded text-xs font-normal text-gray-700"
              >
                <div className="flex items-center space-x-1.5">
                  <BookOpen className="h-3.5 w-3.5" />
                  <span>Knowledge Base</span>
                </div>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Data Source Section */}
            <div className="py-0.5 border-t border-gray-200">
              <button
                onClick={() => navigate('/data-sources')}
                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-100 rounded text-xs font-normal text-gray-700"
              >
                <div className="flex items-center space-x-1.5">
                  <Plug className="h-3.5 w-3.5" />
                  <span>Data Source</span>
                </div>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings - Always at bottom */}
      <div className="border-t border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => {
            // TODO: Open settings modal or navigate to settings
            console.log('Settings clicked');
          }}
          className="w-full flex items-center space-x-1.5 px-3 py-2 hover:bg-gray-100 text-xs font-normal text-gray-700"
        >
          <Settings className="h-4 w-4" />
          <span>Settings</span>
        </button>
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
      />
    </div>
  );
};

export default Sidebar;
